/**
 * 캐릭터 카드 임포트 오케스트레이션 — 결정 #6/#18을 잇는다.
 *
 *  1) core 임포터로 바이트 → 도메인 엔티티(캐릭터 + 임베드 로어북 + 임베드 에셋) 변환.
 *  2) 임베드 로어북이 있으면 저장하고 캐릭터에 연결.
 *  3) PNG 카드면 그 PNG 자체를, CHARX면 embeded:// 아바타를 에셋으로 임포트해 연결.
 *  4) 캐릭터 저장.
 *
 * 저장소/임포터는 core 인터페이스·함수로만 다룬다(결정 #10).
 */
import {
  exportCardToJson,
  importCardFromBytesAsync,
  measurePerformanceAsync,
  type AssetId,
  type Character,
} from "@roqeet/core";
import { storage } from "./storage.js";
import { importAsset, releaseAsset } from "../platform/assets/index.js";

export interface ImportCardOutcome {
  character: Character;
  unmappedKeys: string[];
}

/** 파일(File/Blob)에서 캐릭터 카드를 임포트해 저장한다. */
export async function importCharacterCard(file: Blob, filename?: string): Promise<ImportCardOutcome> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);

  const result = await importCardFromBytesAsync(bytes);
  let character = result.character;

  // 임베드 로어북 저장 + 연결(결정 #6).
  if (result.lorebook) {
    await storage.lorebooks.put(result.lorebook);
  }

  // PNG 카드면 이미지 자체를 아바타로 임포트(결정 #18).
  if (result.fromPng) {
    try {
      const asset = await importAsset(file, filename);
      character = { ...character, avatarAssetId: asset.id };
    } catch {
      // 아바타 실패는 치명적이지 않음 — 캐릭터는 그대로 저장.
    }
  }

  // 임베드 에셋(결정 #6/#18): CHARX의 embeded:// + PNG의 chara-ext-asset_ 청크.
  //  - "icon"/"avatar" → 아바타로 연결.
  //  - "emotion"        → emotionAssetIds[감정명]으로 연결(표정 스크립트용).
  //  - 그 외           → extraAssetIds에 보존(배경 등).
  if (result.assets?.length) {
    character = await measurePerformanceAsync(
      "compose_character_layers",
      {
        inputSize: result.assets.reduce((n, asset) => n + asset.bytes.byteLength, 0),
        fileId: filename ?? "",
        packageId: character.id,
        cache: "miss",
        thread: "main",
        invalidationReason: "character_card_import",
        detail: { assets: result.assets.length },
      },
      async () => {
    const emotionAssetIds: Record<string, AssetId> = { ...(character.emotionAssetIds ?? {}) };
    const extraAssetIds: AssetId[] = [...(character.extraAssetIds ?? [])];
    for (const a of result.assets ?? []) {
      try {
        // TS 5.7 typed-array 호환: fresh ArrayBuffer 백킹으로 복사 후 Blob 생성.
        const buf = new Uint8Array(a.bytes.byteLength);
        buf.set(a.bytes);
        const blob = new Blob([buf], { type: a.mime });
        const asset = await importAsset(blob, a.name);
        if (!character.avatarAssetId && (a.role === "icon" || a.role === "avatar")) {
          // 아바타가 아직 없고 이 에셋이 아이콘(아바타)이면 연결.
          character = { ...character, avatarAssetId: asset.id };
        } else if (a.role === "emotion") {
          // 감정 표현 스프라이트: 이름(감정명) 기준으로 매핑.
          emotionAssetIds[a.name] = asset.id;
        } else {
          // 배경/기타: 손실 없이 보존.
          extraAssetIds.push(asset.id);
        }
      } catch {
        // 개별 에셋 실패는 무시(캐릭터 임포트는 계속).
      }
    }
    character = {
      ...character,
      ...(Object.keys(emotionAssetIds).length ? { emotionAssetIds } : {}),
      ...(extraAssetIds.length ? { extraAssetIds } : {}),
    };
    return character;
      },
    );
  }

  await storage.characters.put(character);
  return { character, unmappedKeys: result.unmappedKeys };
}

/**
 * 캐릭터를 CCv3 JSON 카드로 내보내 파일로 다운로드한다(결정 #6). 캐릭터에
 * 임베드 로어북이 연결돼 있으면 character_book으로 함께 내보낸다.
 */
export async function exportCharacterCard(character: Character): Promise<void> {
  const lorebook = character.lorebookId
    ? await storage.lorebooks.get(character.lorebookId)
    : undefined;
  const json = exportCardToJson(character, lorebook ?? undefined);

  const blob = new Blob([json], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const safeName = character.name.replace(/[^\w가-힣\- ]/g, "_").slice(0, 60) || "character";
  a.href = url;
  a.download = `${safeName}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 캐릭터를 삭제한다(소프트 삭제, 결정 #20). 아바타·감정·기타 에셋 참조도 해제해
 * GC 후보로 만든다(결정 #18). 임베드 로어북은 다른 곳에서 참조될 수 있어 함께
 * 지우지 않는다.
 */
export async function deleteCharacter(character: Character): Promise<void> {
  const assetIds: AssetId[] = [
    ...(character.avatarAssetId ? [character.avatarAssetId] : []),
    ...Object.values(character.emotionAssetIds ?? {}),
    ...(character.extraAssetIds ?? []),
  ];
  for (const id of assetIds) {
    await releaseAsset(id).catch(() => {});
  }
  await storage.characters.delete(character.id);
}
